import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { isSuperAdmin, useMyPermissions } from '../auth/useMyPermissions';
import type { ReactNode } from 'react';

/**
 * Guide links, pinned to the bottom of the sidebar.
 *
 * Three shapes, one per audience: a supplier gets their manual, a TAP member
 * gets the team guide, and a super admin gets a popover holding all three.
 *
 * The detail that is easy to get wrong: /guide is NOT a React route. It is a
 * static file in public/, served by the CDN, so a <Link to="/guide"> would be
 * handled by the router, match nothing, and render the 404 page — a broken
 * link that only breaks in the browser, since a router test would happily
 * report that it navigated. It has to be a real anchor.
 *
 * That anchor opens in a new tab on purpose. Following it in place is a full
 * document load: the SPA unloads and takes any half-typed task or open panel
 * with it, which is a rough thing to do to somebody who clicked "guide"
 * precisely because they were unsure mid-task.
 */

const TEAM_GUIDE_HREF = '/guide';

/** The static team guide. An anchor, never a router Link. */
function TeamGuideAnchor({ children }: { children: ReactNode }) {
  return (
    <a className="shell-guide-link" href={TEAM_GUIDE_HREF} target="_blank" rel="noreferrer">
      {children}
      <span className="shell-guide-note"> (opens in a new tab)</span>
    </a>
  );
}

function Emoji({ children }: { children: string }) {
  // Hidden from the accessible name: "open book emoji User Guide" is noise,
  // and it would land in the middle of every by-name query in the tests too.
  return <span aria-hidden="true">{children}</span>;
}

export function SidebarGuides({ shellKind }: { shellKind: 'tap' | 'supplier' }) {
  const permissions = useMyPermissions();

  if (shellKind === 'supplier') {
    return (
      <div className="shell-footer">
        <Link className="shell-guide-link" to="/supplier/guide">
          <Emoji>📖</Emoji> Supplier Guide
        </Link>
      </div>
    );
  }

  // While the permissions query is in flight, isSuperAdmin() is false for
  // everybody, so this renders the plain link first and swaps to the popover
  // when the answer lands. Chosen over rendering nothing: the single link is
  // correct for almost everyone and never points somewhere wrong, whereas an
  // empty footer that fills in a moment later shifts the whole sidebar.
  if (!isSuperAdmin(permissions.data)) {
    return (
      <div className="shell-footer">
        <TeamGuideAnchor>
          <Emoji>📖</Emoji> User Guide
        </TeamGuideAnchor>
      </div>
    );
  }

  return (
    <div className="shell-footer">
      <GuidesPopover />
    </div>
  );
}

function GuidesPopover() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) {
      triggerRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        close(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close]);

  return (
    <div
      ref={rootRef}
      className="shell-guide-popover"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close(true);
        }
      }}
    >
      {/* The panel is BEFORE the trigger in the DOM as well as above it on
          screen. It opens upward — there is nothing below a footer — and
          keeping source order and visual order the same is what stops keyboard
          focus jumping past the list to reach it. */}
      {open ? (
        <ul id={panelId} className="shell-guide-list" aria-label="Guides and manuals">
          <li>
            <TeamGuideAnchor>
              <Emoji>📘</Emoji> TAP Hub Team Guide
            </TeamGuideAnchor>
          </li>
          <li>
            <Link
              className="shell-guide-link"
              to="/supplier/guide"
              onClick={() => {
                close(false);
              }}
            >
              <Emoji>🏭</Emoji> Supplier Portal Guide
            </Link>
          </li>
          <li>
            <Link
              className="shell-guide-link"
              to="/admin/guide"
              onClick={() => {
                close(false);
              }}
            >
              <Emoji>🔑</Emoji> Super Admin Master Manual
            </Link>
          </li>
        </ul>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        className="shell-guide-link shell-guide-trigger"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Emoji>📖</Emoji> Guides &amp; Manuals
        <span className="shell-guide-caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>
    </div>
  );
}
